import { useEffect, useMemo, useState } from "react";
import { IoClose, IoSearch } from "react-icons/io5";
import cn from "../../../utils/classNames";

interface ContractFieldsSearchProps {
    targetId: string;
    className?: string;
}

interface SearchableField {
    key: string;
    label: string;
    section: string;
}

const normalizeText = (value: string) => {
    return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\*/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
};

const cleanLabel = (value: string) => {
    return value
        .replace(/\*/g, "")
        .replace(/\s+/g, " ")
        .trim();
};

const getElementSelectorById = (id: string) => {
    if (typeof CSS !== "undefined" && CSS.escape) {
        return `#${CSS.escape(id)}`;
    }

    return `#${id.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1")}`;
};

const getVisibleFieldTarget = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null;
    if (element.classList.contains("hidden")) return null;

    let current: HTMLElement | null = element;
    while (current && current !== document.body) {
        if (current.classList.contains("hidden")) return null;
        current = current.parentElement;
    }

    return element;
};

const getFieldTarget = (labelElement: Element): HTMLElement | null => {
    const explicitTarget = labelElement.closest("[data-contract-search-field]") as HTMLElement | null;
    if (explicitTarget) return getVisibleFieldTarget(explicitTarget);

    const phoneTarget = labelElement.closest(".phone-input-wrapper") as HTMLElement | null;
    if (phoneTarget) return getVisibleFieldTarget(phoneTarget);

    const infoTarget = labelElement.closest(".block.min-w-0") as HTMLElement | null;
    if (infoTarget) return getVisibleFieldTarget(infoTarget);

    const label = labelElement.closest("label") as HTMLLabelElement | null;
    if (label?.htmlFor) {
        const field = document.querySelector(getElementSelectorById(label.htmlFor)) as HTMLElement | null;
        const fieldContainer = field?.closest(".rizzui-input-root, .rizzui-textarea-root, [class*='col-span'], [class*='md:col-span'], [class*='lg:col-span']") as HTMLElement | null;
        if (fieldContainer) return getVisibleFieldTarget(fieldContainer);
    }

    const labeledContainer = label?.parentElement || labelElement.parentElement;
    return getVisibleFieldTarget(labeledContainer as HTMLElement | null);
};

const getNearestBlockTitle = (fieldTarget: HTMLElement, root: HTMLElement): string => {
    let current: HTMLElement | null = fieldTarget;

    while (current && current !== root) {
        const childWithTitle = Array.from(current.children).find((child) => child.querySelector("h3"));
        const title = childWithTitle?.querySelector("h3")?.textContent;
        if (title) return cleanLabel(title);
        current = current.parentElement;
    }

    return "";
};

const getFieldSection = (fieldTarget: HTMLElement, root: HTMLElement): string => {
    let current: HTMLElement | null = fieldTarget;
    let groupTitle = "";

    while (current && current !== root) {
        const formGroupTitle = current.previousElementSibling?.querySelector("h4")?.textContent;
        if (formGroupTitle) {
            groupTitle = cleanLabel(formGroupTitle);
            break;
        }
        current = current.parentElement;
    }

    const blockTitle = getNearestBlockTitle(fieldTarget, root);

    if (blockTitle && groupTitle) return `${blockTitle} - ${groupTitle}`;
    return blockTitle || groupTitle;
};

const getLabelElements = (root: HTMLElement) => {
    const candidates = Array.from(root.querySelectorAll("label, .rizzui-input-label")) as HTMLElement[];
    const labels = new Set<HTMLElement>();

    candidates.forEach((candidate) => {
        if (candidate.matches("label")) {
            labels.add((candidate.querySelector(".rizzui-input-label") as HTMLElement | null) || candidate);
            return;
        }

        labels.add(candidate);
    });

    return Array.from(labels);
};

const ContractFieldsSearch = ({ targetId, className }: ContractFieldsSearchProps) => {
    const [query, setQuery] = useState("");
    const [fields, setFields] = useState<SearchableField[]>([]);
    const [focusedKey, setFocusedKey] = useState<string | null>(null);

    const collectFields = () => {
        const root = document.getElementById(targetId);
        if (!root) {
            setFields([]);
            return;
        }

        const labelElements = getLabelElements(root);
        const nextFields: SearchableField[] = [];
        const seenTargets = new Set<HTMLElement>();

        labelElements.forEach((labelElement, index) => {
            const label = cleanLabel(labelElement.textContent || "");
            const target = getFieldTarget(labelElement);

            if (!label || !target || seenTargets.has(target)) return;

            const key = `contract-field-${index}`;
            target.dataset.contractFieldSearchKey = key;
            seenTargets.add(target);
            nextFields.push({
                key,
                label,
                section: getFieldSection(target, root),
            });
        });

        setFields(nextFields);
    };

    useEffect(() => {
        collectFields();

        const root = document.getElementById(targetId);
        if (!root) return;

        const observer = new MutationObserver(() => collectFields());
        observer.observe(root, { childList: true, subtree: true });

        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId]);

    const filteredFields = useMemo(() => {
        const normalizedQuery = normalizeText(query);
        if (!normalizedQuery) return [];

        return fields.filter((field) => {
            return normalizeText(`${field.section} ${field.label}`).includes(normalizedQuery);
        });
    }, [fields, query]);

    const handleSelectField = (key: string) => {
        const root = document.getElementById(targetId);
        const target = root?.querySelector(`[data-contract-field-search-key="${key}"]`) as HTMLElement | null;
        if (!target) return;

        setFocusedKey(key);
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.classList.add("contract-field-search-highlight");

        const focusableElement = target.querySelector("input, textarea, select, button, [tabindex]:not([tabindex='-1'])") as HTMLElement | null;
        setTimeout(() => focusableElement?.focus?.(), 250);
        setTimeout(() => {
            target.classList.remove("contract-field-search-highlight");
            setFocusedKey(null);
        }, 1600);
    };

    const clearSearch = () => {
        setQuery("");
        setFocusedKey(null);
    };

    return (
        <div className={cn("rounded-md border border-gray-200 bg-white p-4 shadow-sm", className)}>
            <label htmlFor={`${targetId}-field-search`} className="rizzui-input-label mb-1.5 block text-sm font-semibold text-gray-800">
                Buscador de campos
            </label>
            <div className="relative">
                <IoSearch className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden />
                <input
                    id={`${targetId}-field-search`}
                    type="search"
                    value={query}
                    onFocus={collectFields}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Buscar por nombre de campo..."
                    className="h-10 w-full rounded-md border border-gray-200 bg-white py-2 pl-10 pr-10 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-primary focus:ring-[0.8px] focus:ring-primary"
                    autoComplete="off"
                />
                {query && (
                    <button
                        type="button"
                        onClick={clearSearch}
                        className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        aria-label="Limpiar búsqueda"
                    >
                        <IoClose className="h-4 w-4" aria-hidden />
                    </button>
                )}
            </div>

            {query && (
                <div className="mt-3 overflow-hidden rounded-md border border-gray-200 bg-white">
                    {filteredFields.length > 0 ? (
                        <div className="max-h-56 overflow-y-auto">
                            {filteredFields.map((field) => (
                                <button
                                    key={field.key}
                                    type="button"
                                    onClick={() => handleSelectField(field.key)}
                                    className={cn(
                                        "flex w-full flex-col items-start border-b border-gray-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-primary/10",
                                        focusedKey === field.key && "bg-primary/10"
                                    )}
                                >
                                    <span className="text-sm font-semibold text-gray-900">{field.label}</span>
                                    {field.section && (
                                        <span className="mt-0.5 text-xs text-gray-500">{field.section}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="px-3 py-3 text-sm text-gray-500">
                            No se han encontrado campos con esa búsqueda.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContractFieldsSearch;
